import { ERAS, type EngineSound } from '../config/eras';

/**
 * All sound is synthesized with Web Audio (no files, no network): the engine
 * (pitch follows rpm, voiced per era: firing pulses, supercharger whine, turbo
 * whistle and blow-off), tire squeal, curb rumble, gravel, wind, and the crowd.
 */
export interface AudioFrame {
  rpm: number;
  redline: number;
  throttle: number;
  speed: number;
  /** 0..1 how hard the tires are sliding (asphalt only). */
  squeal: number;
  curb: boolean;
  /** 0..1 fraction of wheels on grass/gravel; gravel is louder and grittier. */
  offroad: number;
  gravel: boolean;
  /** 0..1 how close the grandstand / spectators are. */
  crowd: number;
}

function noiseBuffer(ctx: AudioContext, seconds: number, pink: boolean): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < len; i++) {
    const w = Math.random() * 2 - 1;
    if (!pink) {
      d[i] = w;
      continue;
    }
    b0 = 0.99886 * b0 + w * 0.0555179;
    b1 = 0.99332 * b1 + w * 0.0750759;
    b2 = 0.969 * b2 + w * 0.153852;
    b3 = 0.8665 * b3 + w * 0.3104856;
    b4 = 0.55 * b4 + w * 0.5329522;
    b5 = -0.7616 * b5 - w * 0.016898;
    d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
    b6 = w * 0.115926;
  }
  return buf;
}

/** How loud a full slide squeals (a full slide sits just under the engine). */
const SQUEAL_LEVEL = 0.14;

/** How hard the engine voice is clipped, relative to each era's own grit (the old full grit was a buzz saw). */
const ENGINE_GRIT = 0.5;

function distortionCurve(amount: number): Float32Array<ArrayBuffer> {
  const n = 1024;
  const c = new Float32Array(new ArrayBuffer(n * 4));
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    c[i] = ((1 + amount) * x) / (1 + amount * Math.abs(x));
  }
  return c;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  /** Everything the car makes (engine, tyres, wind...): silenced while paused or on the title card. */
  private carBus!: GainNode;
  private carOn = true;
  private muted = false;
  /** Master level: an engine is always on, so it sits well back (players kept muting the old, louder mix). */
  private volume = 0.5;
  private eng!: { o1: OscillatorNode; o2: OscillatorNode; o3: OscillatorNode; shaper: WaveShaperNode; filter: BiquadFilterNode; smooth: BiquadFilterNode; gain: GainNode };
  private sound: EngineSound = ERAS['1961'].sound;
  private whine!: { osc: OscillatorNode; gain: GainNode };
  private turbo!: { osc: OscillatorNode; gain: GainNode; blowoff: GainNode };
  private boost = 0;
  private lastThrottle = 0;
  private lastTime = 0;
  private squeal!: { gain: GainNode; voices: { osc: OscillatorNode; ratio: number; drift: number }[]; tone: BiquadFilterNode; hiss: BiquadFilterNode };
  private rumble!: { gain: GainNode; lfo: OscillatorNode };
  private dirt!: { gain: GainNode; filter: BiquadFilterNode };
  private wind!: { gain: GainNode; filter: BiquadFilterNode };
  private crowd!: { gain: GainNode; filter: BiquadFilterNode };
  private cheerUntil = 0;
  private lastSqueal = 0;

  constructor() {
    // Remember a muted game between visits.
    try {
      this.muted = localStorage.getItem('gp.muted') === '1';
    } catch {
      this.muted = false;
    }
  }

  get started(): boolean {
    return this.ctx !== null;
  }

  /** Sound on (from a tap or a key: browsers only let sound start then). */
  async start(): Promise<void> {
    this.prepare();
    if (this.ctx && this.ctx.state !== 'running') await this.ctx.resume();
  }

  /**
   * Open the audio device and build the sounds, silent until start(). Opening the device takes a moment
   * (a third of a second on some machines), so the game does it while loading rather than in the tap that
   * starts a race, where it froze the screen.
   */
  prepare(): void {
    if (this.ctx) return;
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    let ctx: AudioContext;
    try {
      ctx = new Ctx();
    } catch {
      return;
    }
    this.build(ctx);
  }

  /**
   * The same sounds drawn into an offline context instead (the trailer's soundtrack, src/film.ts), on its
   * own clock rather than the wall clock.
   */
  offline(ctx: BaseAudioContext): void {
    this.clock = () => ctx.currentTime;
    this.build(ctx as AudioContext);
  }

  /** The clock the turbo's boost runs on. */
  private clock = (): number => performance.now() / 1000;

  private build(ctx: AudioContext): void {
    this.ctx = ctx;
    // A safety limiter only. Web Audio's compressor adds its own make-up gain (the old settings
    // pushed everything up about 7 dB); the trim after it takes that back out.
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -6;
    comp.knee.value = 4;
    comp.ratio.value = 12;
    comp.attack.value = 0.003;
    comp.release.value = 0.25;
    const trim = ctx.createGain();
    trim.gain.value = Math.pow(10, (-0.6 * (6 - 6 / 12)) / 20);
    comp.connect(trim).connect(ctx.destination);
    this.master = ctx.createGain();
    this.master.gain.value = this.muted ? 0 : this.volume;
    this.master.connect(comp);
    this.carBus = ctx.createGain();
    this.carBus.gain.value = this.carOn ? 1 : 0;
    this.carBus.connect(this.master);

    const white = noiseBuffer(ctx, 2, false);
    const pink = noiseBuffer(ctx, 3, true);
    const loop = (buf: AudioBuffer): AudioBufferSourceNode => {
      const s = ctx.createBufferSource();
      s.buffer = buf;
      s.loop = true;
      s.start(0, Math.random() * buf.duration);
      return s;
    };

    // Engine: two detuned saws an octave apart and a soft triangle an octave below
    // (the body), through a gentle clipper and two low-passes that open with rpm
    // and throttle. Warm rather than buzzy: phone speakers make the top end shrill.
    {
      const o1 = ctx.createOscillator();
      o1.type = 'sawtooth';
      const o2 = ctx.createOscillator();
      o2.type = 'sawtooth';
      o2.detune.value = -8;
      const o3 = ctx.createOscillator();
      o3.type = 'triangle';
      const g1 = ctx.createGain();
      g1.gain.value = 0.4;
      const g2 = ctx.createGain();
      g2.gain.value = 0.36;
      const g3 = ctx.createGain();
      g3.gain.value = 0.22;
      const shaper = ctx.createWaveShaper();
      shaper.curve = distortionCurve(this.sound.grit * ENGINE_GRIT);
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.Q.value = 1.3;
      const smooth = ctx.createBiquadFilter();
      smooth.type = 'lowpass';
      smooth.Q.value = 0.6;
      smooth.frequency.value = 3000;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      o1.connect(g1).connect(shaper);
      o2.connect(g2).connect(shaper);
      o3.connect(g3).connect(shaper);
      shaper.connect(filter).connect(smooth).connect(gain).connect(this.carBus);
      o1.start();
      o2.start();
      o3.start();
      this.eng = { o1, o2, o3, shaper, filter, smooth, gain };
    }

    // Supercharger whine: a thin tone geared to the crank (silent on eras without a blower).
    {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      const gain = ctx.createGain();
      gain.gain.value = 0;
      osc.connect(gain).connect(this.carBus);
      osc.start();
      this.whine = { osc, gain };
    }

    // Turbo: a whistle that spools up with boost, and a blow-off hiss when you lift.
    {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      const gain = ctx.createGain();
      gain.gain.value = 0;
      osc.connect(gain).connect(this.carBus);
      osc.start();
      const hiss = loop(white);
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 2600;
      bp.Q.value = 0.8;
      const blowoff = ctx.createGain();
      blowoff.gain.value = 0;
      hiss.connect(bp).connect(blowoff).connect(this.carBus);
      this.turbo = { osc, gain, blowoff };
    }

    // Tyre squeal: the tread vibrating as it slides, so a tone, not a hiss. Four
    // voices a little out of tune with each other (each tyre sings its own note),
    // rich in overtones, each drifting slowly and at random (never a regular
    // wobble: that's a siren), shaped by a broad band, with a whisper of hiss.
    {
      const tone = ctx.createBiquadFilter();
      tone.type = 'bandpass';
      tone.Q.value = 0.9;
      tone.frequency.value = 1700;
      const top = ctx.createBiquadFilter();
      top.type = 'lowpass';
      top.frequency.value = 2400;
      top.Q.value = 0.5;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      const voices: { osc: OscillatorNode; ratio: number; drift: number }[] = [];
      for (const [ratio, type, g] of [
        [1, 'sawtooth', 0.36],
        [1.061, 'sawtooth', 0.28],
        [1.124, 'triangle', 0.34],
      ] as const) {
        const osc = ctx.createOscillator();
        osc.type = type;
        osc.frequency.value = 520 * ratio;
        const vg = ctx.createGain();
        vg.gain.value = g;
        osc.connect(vg).connect(tone);
        osc.start();
        voices.push({ osc, ratio, drift: 0 });
      }
      const hissSrc = loop(white);
      const hiss = ctx.createBiquadFilter();
      hiss.type = 'bandpass';
      hiss.frequency.value = 2000;
      hiss.Q.value = 2;
      const hissGain = ctx.createGain();
      hissGain.gain.value = 0.05;
      hissSrc.connect(hiss).connect(hissGain).connect(tone);
      tone.connect(top).connect(gain).connect(this.carBus);
      this.squeal = { gain, voices, tone, hiss };
    }

    // Curb rumble: low noise chopped at the stripe frequency.
    {
      const src = loop(pink);
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 260;
      const chop = ctx.createGain();
      chop.gain.value = 0.5;
      const lfo = ctx.createOscillator();
      lfo.type = 'square';
      lfo.frequency.value = 12;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.5;
      lfo.connect(lfoGain).connect(chop.gain);
      const gain = ctx.createGain();
      gain.gain.value = 0;
      src.connect(lp).connect(chop).connect(gain).connect(this.carBus);
      lfo.start();
      this.rumble = { gain, lfo };
    }

    // Grass / gravel.
    {
      const src = loop(white);
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.Q.value = 0.9;
      filter.frequency.value = 600;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      src.connect(filter).connect(gain).connect(this.carBus);
      this.dirt = { gain, filter };
    }

    // Wind.
    {
      const src = loop(pink);
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 500;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      src.connect(filter).connect(gain).connect(this.carBus);
      this.wind = { gain, filter };
    }

    // Crowd murmur, with a slow swell.
    {
      const src = loop(pink);
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 750;
      filter.Q.value = 0.7;
      const swell = ctx.createGain();
      swell.gain.value = 0.8;
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.23;
      const lg = ctx.createGain();
      lg.gain.value = 0.2;
      lfo.connect(lg).connect(swell.gain);
      lfo.start();
      const gain = ctx.createGain();
      gain.gain.value = 0.02;
      src.connect(filter).connect(swell).connect(gain).connect(this.master);
      this.crowd = { gain, filter };
    }

    // Cicadas: a thin, pulsing high hiss (Tuscan summer).
    {
      const src = loop(white);
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 5200;
      bp.Q.value = 6;
      const am = ctx.createGain();
      am.gain.value = 0.5;
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 23;
      const lg = ctx.createGain();
      lg.gain.value = 0.5;
      lfo.connect(lg).connect(am.gain);
      lfo.start();
      const gain = ctx.createGain();
      gain.gain.value = 0.006;
      src.connect(bp).connect(am).connect(gain).connect(this.master);
    }
  }

  /** Car sounds on while driving, faded out on pause and the title card (the crowd and cicadas play on). */
  setCarSounds(on: boolean): void {
    this.carOn = on;
    if (this.ctx) this.carBus.gain.setTargetAtTime(on ? 1 : 0, this.ctx.currentTime, 0.06);
  }

  /** The day's engine voice. */
  setEngine(sound: EngineSound): void {
    this.sound = sound;
    this.boost = 0;
    if (this.ctx) this.eng.shaper.curve = distortionCurve(sound.grit * ENGINE_GRIT);
  }

  setMuted(m: boolean): void {
    this.muted = m;
    try {
      localStorage.setItem('gp.muted', m ? '1' : '0');
    } catch {
      // No storage: it just won't be remembered.
    }
    if (this.ctx) this.master.gain.setTargetAtTime(m ? 0 : this.volume, this.ctx.currentTime, 0.05);
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /** Called every rendered frame. */
  update(f: AudioFrame): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime;
    const tau = 0.035;
    const set = (p: AudioParam, v: number, k = tau): void => {
      p.setTargetAtTime(v, t, k);
    };

    // Wall clock for the boost simulation: the audio clock can lag real time.
    const now = this.clock();
    const dt = Math.min(0.1, Math.max(0, now - this.lastTime));
    this.lastTime = now;

    // Firing pulses per revolution set the note: a four is gruff, a V10 screams.
    const snd = this.sound;
    const r = Math.max(800, f.rpm);
    const fire = (r / 60) * snd.firingPerRev;
    set(this.eng.o1.frequency, fire * 0.5);
    set(this.eng.o2.frequency, fire);
    set(this.eng.o3.frequency, fire * 0.25);
    const rn = r / f.redline;
    // Never below ~700 Hz: phone speakers play little under 300 Hz, so the voice has to live in the mids.
    const cutoff = (700 + rn * 1300 + f.throttle * 600) * Math.min(1.15, snd.brightness);
    set(this.eng.filter.frequency, cutoff);
    set(this.eng.smooth.frequency, cutoff * 1.4);
    // Off the throttle it drops right back; on it, it's present but never shouty.
    set(this.eng.gain.gain, 0.045 + f.throttle * 0.06 + rn * 0.035);

    if (snd.whine) {
      set(this.whine.osc.frequency, (r / 60) * snd.whine.ratio);
      set(this.whine.gain.gain, snd.whine.gain * 0.6 * (0.35 + 0.65 * f.throttle) * (0.3 + rn), 0.05);
    } else set(this.whine.gain.gain, 0);

    if (snd.turbo) {
      // Boost builds slowly on throttle and collapses when you lift: the pssh.
      const on = f.throttle > 0.5;
      this.boost += ((on ? rn : 0) - this.boost) * Math.min(1, dt * (on ? 1.3 : 6));
      set(this.turbo.osc.frequency, 1900 + this.boost * 4600, 0.05);
      set(this.turbo.gain.gain, this.boost * this.boost * 0.022, 0.05);
      if (snd.blowoff !== false && this.lastThrottle > 0.6 && f.throttle < 0.3 && this.boost > 0.4) {
        this.turbo.blowoff.gain.cancelScheduledValues(t);
        this.turbo.blowoff.gain.setValueAtTime(0.09 * this.boost, t);
        this.turbo.blowoff.gain.setTargetAtTime(0, t + 0.04, 0.12);
      }
    } else {
      this.boost = 0;
      set(this.turbo.gain.gain, 0);
    }
    this.lastThrottle = f.throttle;

    const sp = Math.min(1, f.speed / 60);
    // Squeal: nothing for a hint of slip, then growing; its pitch rides the speed (it falls as you
    // scrub off speed: the classic eeeerr under braking) and rises a touch the harder you slide.
    const sq = Math.max(0, f.squeal) * Math.min(1, f.speed / 8);
    const level = sq * Math.sqrt(sq);
    set(this.squeal.gain.gain, level * SQUEAL_LEVEL, level > this.lastSqueal ? 0.03 : 0.09);
    this.lastSqueal = level;
    const f0 = 420 + Math.min(f.speed, 70) * 3.4 + sq * 40;
    for (const v of this.squeal.voices) {
      // Each voice wanders on its own: a slow random walk pulled back toward its note.
      v.drift += (Math.random() - 0.5) * 0.004 - v.drift * 0.04;
      set(v.osc.frequency, f0 * v.ratio * (1 + v.drift), 0.06);
    }
    set(this.squeal.tone.frequency, f0 * 1.35, 0.1);
    set(this.squeal.hiss.frequency, f0 * 1.6, 0.1);

    set(this.rumble.gain.gain, f.curb && f.speed > 3 ? 0.22 : 0, 0.03);
    set(this.rumble.lfo.frequency, Math.max(4, f.speed / 2));

    const dirt = f.offroad * Math.min(1, f.speed / 15);
    set(this.dirt.gain.gain, dirt * (f.gravel ? 0.22 : 0.13), 0.05);
    set(this.dirt.filter.frequency, f.gravel ? 1100 : 450);

    set(this.wind.gain.gain, sp * sp * 0.09, 0.2);
    set(this.wind.filter.frequency, 300 + sp * 1400, 0.2);

    const cheer = t < this.cheerUntil ? 1 : 0;
    set(this.crowd.gain.gain, 0.012 + f.crowd * 0.07 + cheer * 0.12, cheer ? 0.15 : 0.6);
    set(this.crowd.filter.frequency, cheer ? 1300 : 750, 0.3);
  }

  cheer(seconds = 2.5): void {
    if (this.ctx) this.cheerUntil = this.ctx.currentTime + seconds;
  }

  /** A dull thud for barrier hits. */
  thud(strength: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const o = ctx.createOscillator();
    o.type = 'sine';
    const g = ctx.createGain();
    const t = ctx.currentTime;
    o.frequency.setValueAtTime(110, t);
    o.frequency.exponentialRampToValueAtTime(40, t + 0.25);
    g.gain.setValueAtTime(Math.min(0.35, strength * 0.035), t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + 0.32);
  }
}
